<template>
  <v-container fluid>
    <v-card class="mb-4">
      <v-card-title class="pb-0">
        Selected Pepper Flow Filter
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense
                class="py-0">
          <v-list-item>
            <v-list-item-icon>
              <v-icon>
                fal fa-plug
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              {{ connection.name }}
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
        </v-list>
      </div>
    </v-card>

    <v-card v-if="cart.importAll"
            class="mb-4">
      <v-card-title class="pb-0">
        Documents will be imported from Pepper Flow:
      </v-card-title>
      <div class="pa-4">
        All current and future documents from the {{ connection.name }} filter will be imported from Pepper Flow.
      </div>
    </v-card>
    <v-card v-else
            class="mb-4">
      <v-card-title v-if="cart.documents.length > 1"
                    class="pb-0">
        These {{ cart.documents.length | pluralize('document') }} will be imported from Pepper Flow:
      </v-card-title>
      <v-card-title v-else
                    class="pb-0">
        This document will be imported from Pepper Flow:
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense
                class="py-0">
          <template v-for="document in cart.documents">
            <v-list-item :key="document.id">
              <v-list-item-icon>
                <v-icon>
                  fal fa-file
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content v-if="isDemo">
                ***********************************
              </v-list-item-content>
              <v-list-item-content v-else>
                {{ document.display_name }} ({{ document.document_id }})
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
          </template>
        </v-list>
      </div>
    </v-card>

    <v-card class="mb-4">
      <v-card-title v-if="cart.groups.length > 1"
                    class="pb-0">
        Members of these groups will have access to the {{ cart.documents.length | pluralize('document', false) }}:
      </v-card-title>
      <v-card-title v-else
                    class="pb-0">
        Members of this group will have access to the {{ cart.documents.length | pluralize('document', false) }}:
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense
                class="py-0">
          <template v-for="group in sortedGroups">
            <v-list-item :key="group.id">
              <v-list-item-icon>
                <v-icon>
                  fal fa-users
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                {{ group.name }}
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
          </template>
        </v-list>
      </div>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="pb-0">
        A Mobile Locker user:
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense
                class="py-0">
          <permission-list-item :permission="cart.permissions.can_be_shared"
                                can="share via email.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.can_be_shared_link"
                                can="create a link that can be shared elsewhere.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.can_be_presented_by_user"
                                can="present from the browser.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.can_be_annotated"
                                can="annotate in the iOS app.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.is_rateable"
                                can="rate in the iOS app.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.can_be_exported"
                                can="export from the browser or desktop app.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.can_be_printed_by_user"
                                can="print from the browser or desktop app.">
          </permission-list-item>
          <v-divider></v-divider>
        </v-list>
      </div>
    </v-card>

    <v-card class="mb-4"
            v-if="form.permissions.can_be_shared || form.permissions.can_be_shared_link">
      <v-card-title class="pb-0">
        Viewers will be able to:
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense
                class="py-0">
          <permission-list-item :permission="cart.permissions.can_be_downloaded"
                                can="download each of these files.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.can_be_printed"
                                can="print each of these files.">
          </permission-list-item>
          <v-divider></v-divider>
          <permission-list-item :permission="cart.permissions.can_be_forwarded"
                                can="forward each of these files to someone else.">
          </permission-list-item>
          <v-divider></v-divider>
        </v-list>
      </div>
    </v-card>

    <v-card class="mb-4"
            v-if="form.permissions.can_be_shared || form.permissions.can_be_shared_link">
      <v-card-title class="pb-0">
        Share Theme
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense
                class="py-0">
          <v-list-item>
            <v-list-item-icon>
              <v-icon>
                fal fa-presentation
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              {{ shareTheme ? shareTheme.name : 'None selected so the default will be used.' }}
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
        </v-list>
      </div>
    </v-card>

    <v-card class="mb-4"
            v-if="form.permissions.can_be_shared">
      <v-card-title class="pb-0">
        Quick Share Email Template
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense>
          <v-list-item>
            <v-list-item-icon>
              <v-icon>
                fal fa-paper-plane
              </v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              {{ shareTemplate ? shareTemplate.name : 'None selected so the default will be used.' }}
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
        </v-list>
      </div>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="pb-0">
        The {{ cart.documents.length | pluralize('document', false) }} will be in this folder:
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-treeview dense
                    :active="activeFolders"
                    :items="rootFolders"
                    item-key="id"
                    item-text="name"
                    item-children="children"
                    open-all
                    readonly>
          <template v-slot:prepend="{item, active, open}">
            <v-icon v-if="item.is_home">
              fas fa-home
            </v-icon>
            <v-icon v-else-if="active || open">
              fas fa-folder-open
            </v-icon>
            <v-icon v-else>
              fas fa-folder
            </v-icon>
          </template>
        </v-treeview>
      </div>
    </v-card>

    <v-card class="mb-4">
      <v-card-title v-if="selectedLabels.length !== 1"
                    class="pb-0">
        These tags will be applied to the {{ cart.documents | pluralize('document', false) }}:
      </v-card-title>
      <v-card-title v-else
                    class="pb-0">
        This tag will be applied to the {{ cart.documents | pluralize('document', false) }}:
      </v-card-title>
      <div class="pa-4 pt-0">
        <v-list dense>
          <template v-for="label in selectedLabels">
            <v-list-item :key="label.id">
              <v-list-item-icon>
                <v-icon>
                  fal fa-tag
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                {{ label.name }}
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
          </template>
          <template v-if="!selectedLabels.length">
            <v-list-item>
              <v-list-item-icon>
                <v-icon>
                  fal fa-tags
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                No tags will be applied.
              </v-list-item-content>
            </v-list-item>
            <v-divider></v-divider>
          </template>
        </v-list>
      </div>
    </v-card>

    <v-form @submit.prevent="submit"
            :disabled="form.busy">
      <v-card class="mb-4">
        <v-card-title class="pb-0">
          Task Settings
        </v-card-title>
        <div class="pa-4 pt-0">

          <v-text-field v-model="form.name"
                        label="Import Task Name"
                        placeholder="Provide a unique name for this import task"
                        :rules="[rules.required]"
                        :error-messages="form.errors.get('name')"
                        required>
          </v-text-field>

          <v-textarea v-model="form.description"
                      v-if="1===2"
                      label="Import Task Description (Optional)"
                      placeholder="Describe this import task"
                      rows="3"
                      :error-messages="form.errors.get('name')">
          </v-textarea>

          <v-switch v-model="form.settings.synchronize_name"
                    label="Keep names in sync with Pepper Flow."
                    dense
                    hide-details>
          </v-switch>

          <v-switch v-model="form.settings.synchronize_expiration_date"
                    label="Keep expiration dates in sync with Pepper Flow."
                    dense
                    hide-details>
          </v-switch>

          <v-switch v-model="form.is_active"
                    label="Run this task immediately."
                    dense
                    hide-details>
          </v-switch>
        </div>
        <v-card-actions>
          <v-btn @click.prevent="$store.dispatch('pepperFlowCart/prevStep')">
            Go back
          </v-btn>
          <v-btn type="submit"
                 color="primary"
                 :disabled="form.busy || !form.name">
            Import
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-container>
</template>
<script>
import {mapGetters} from 'vuex';
import {find, forOwn, orderBy} from 'lodash';
import {Form} from 'vform';
import PermissionListItem from '../../../components/PermissionListItem';
import formValidation from '../../../plugins/formValidation';
import pepperFlowService from '../../../services/pepperFlowService';

export default {
  components: {PermissionListItem},
  mixins: [formValidation],
  data() {
    return {
      form: new Form({
        connection: null,
        description: null,
        documents: [],
        folder: null,
        show_in_main_list: false, // MLD-3968
        groups: [],
        import_all: false, // MLD-2686
        labels: [],
        name: null,
        status: 'Active',
        is_active: true,
        permissions: pepperFlowService.initPermissions(),
        settings: {
          synchronize_name: true,
          synchronize_expiration_date: true,
        },
        share_template_id: null,
        share_theme_id: null,
      }),
    }
  },
  computed: {
    ...mapGetters([
      'folders',
      'groups',
      'labels',
      'shareTemplates',
      'shareThemes',
      'user',
    ]),
    ...mapGetters('content', [
      'homeFolder', // MLD-3968
      'rootFolders',
    ]),
    ...mapGetters('pepperFlow', [
      'connections',
    ]),
    ...mapGetters('pepperFlowCart', [
      'cart',
    ]),
    isDemo() { // MLD-2944
      return this.form.connection === 'demo_vault';
    },
    activeFolders() {
      if (this.cart.folder) {
        return [this.cart.folder];
      }
      return [];
    },
    connection() {
      return find(this.connections, c => c.id === this.cart.connection);
    },
    sortedGroups() {
      const groups = this.groups.filter(g => this.cart.groups.includes(g.id));
      return orderBy(groups, ['slug'], ['asc']);
    },
    selectedLabels() {
      return this.labels.filter(l => this.cart.labels.includes(l.id));
    },
    shareTemplate() {
      return find(this.shareTemplates, s => s.id === this.form.share_template_id);
    },
    shareTheme() {
      return find(this.shareThemes, s => s.id === this.form.share_theme_id);
    }
  },
  created() {
    this.initForm();
  },
  methods: {
    initForm() {
      this.form.name = `${this.user.name} - ${Date.now()}`
      this.form.connection = this.cart.connection;
      this.form.documents = this.cart.documents.map(d => d.id);
      this.form.folder = this.cart.folder;
      this.form.show_in_main_list = this.cart.showInMainList; // MLD-3968
      this.form.groups = this.cart.groups;
      this.form.import_all = this.cart.importAll; // MLD-2686
      this.form.labels = this.cart.labels;
      forOwn(this.cart.permissions, (value, key) => {
        if (this.form.permissions.hasOwnProperty(key)) {
          this.form.permissions[key] = value;
        }
      });
      this.form.share_template_id = this.cart.shareTemplateID;
      this.form.share_theme_id = this.cart.shareThemeID;
    },
    submit() {
      const form = this.form.data();
      if (form.folder === this.homeFolder.id) { // MLD-3649 the folder field can be blank.
        form.folder = null;
      }
      this.form.status = this.form.is_active ? 'Active' : 'Inactive';
      this.form.post(`/api/vodori/pepper-flow/tasks`, {data: form})
        .then(response => {
          this.$store.dispatch('pepperFlowCart/reset');
          this.$router.push({name: 'admin.pf.tasks'});
        })
    }
  }
}
</script>
